import type { Task } from '@dyspatch/core';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { Age, PrMark, RepoChip, RowSkeleton, TaskKey } from '@/components/common/bits';
import { SortMenu } from '@/components/common/SortMenu';
import { TaskStateChip } from '@/components/common/StatusIcon';
import { SplitPane } from '@/components/layout/Page';
import { Menu } from '@/components/ui/menu';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { PausedChip } from '@/features/actions/PauseActions';
import { StepBoard } from '@/features/tasks/StepBoard';
import { TaskDetail } from '@/features/tasks/TaskDetail';
import { useTasks } from '@/hooks/use-fleet';
import { useBoardLayout, useWideLayout } from '@/hooks/use-media-query';
import { type RowProps, useRowKeys } from '@/hooks/use-row-keys';
import { useSort } from '@/hooks/use-sort';
import {
  DEFAULT_TASK_RANGE,
  DEFAULT_TASK_STATUS,
  holdNeedsAPerson,
  isTaskRange,
  isTaskStatusFilter,
  reposOf,
  TASK_RANGES,
  TASK_STATUS_FILTERS,
  taskNote,
  taskPageGroups,
} from '@/lib/fleet';
import { clockTime, minutesSince } from '@/lib/format';
import { cn } from '@/lib/utils';
import { sortedTaskGroups, taskSortOptions } from '@/lib/work-sort';
import type { JobsSearch } from '@/router';

const ALL_REPOS = '__all__';

/**
 * The task board, folded into the Jobs page (issue #60).
 *
 * It used to be `/tasks`, a page of its own with the "Steps" nav entry that
 * went with it. Two entries for one hierarchy -- a job has tasks, and nothing
 * else does -- was the whole complaint, so the board is a view on `/jobs` now
 * (`?board=1`), reached from the same one nav entry the jobs list is, and
 * `/tasks` redirects here rather than 404ing so a saved link keeps working
 * (`router.tsx`).
 *
 * Every group the filter can hold, each with its own empty line -- because "the
 * queue is empty" and "nothing has failed" are both facts worth stating, and a
 * page that silently drops the groups it has nothing for makes a person wonder
 * whether it looked. Filtering to one state is the exception: the four headings
 * that cannot hold it are not facts, they are noise.
 *
 * A gate row wears a 2px accent edge on its left, which is the only place in a
 * list where the accent appears. It is enough to find the rows that need you from
 * across a room, and quiet enough that forty rows still read as one table.
 *
 * `children` is a render prop rather than a plain body, because the header
 * that carries `sub` and the filter menus lives one level up, in the same
 * `ViewHeader` the jobs list uses -- there is one "Jobs" title on this page,
 * not two headers stacked. Everything the board computes (its own filters,
 * sort and roving focus) has exactly one call site either way, here, so the
 * header's menus and the body below never read two different states.
 */
function TaskRow({
  task,
  selected,
  rowProps,
}: {
  task: Task;
  selected: boolean;
  /** The list's roving focus; the row is its own link, so Enter needs no help. */
  rowProps: RowProps;
}) {
  const isGate = task.status === 'review';
  // The tint and the warn-coloured sub-line are for a row somebody has to end:
  // paused, or the one hold that does not clear itself. A task waiting for a
  // slot to free says so in its note and is not lit like a problem (issue #12).
  const held = task.status === 'queued' && (holdNeedsAPerson(task) || Boolean(task.pausedAt));
  const closed = task.status === 'done' || task.status === 'cancelled';
  const note = taskNote(task);

  return (
    <li>
      <Link
        to="/jobs"
        {...rowProps}
        // A row opens the peek beside the list rather than leaving for the
        // task's own page (issue #61): the board is for scanning, and losing
        // the board on every row you look at is what the pane exists to
        // prevent. The peek's own header links on to the page. The selection
        // keeps the filters that found it, so the list behind the pane does
        // not silently become a different list.
        search={(previous: JobsSearch) => ({ ...previous, task: task.id })}
        // Reading, not navigating: Back leaves the board rather than walking
        // out through every task glanced at on the way here.
        replace
        className={cn(
          'relative flex items-start gap-3 px-gutter py-3 transition-colors duration-100',
          'hover:bg-raised/60',
          selected && 'bg-sel',
          held && !selected && 'bg-warn-bg/25',
          closed && 'opacity-60',
        )}
      >
        {isGate ? <span className="bg-gate absolute inset-y-0 left-0 w-[3px]" aria-hidden /> : null}
        {/* The shared state chip (issue #64), not a bare icon (issue #100):
            the same chip a task's page and its peek panel draw, so a task's
            state reads the same word wherever it is looked at. */}
        <TaskStateChip status={task.status} className="mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-baseline gap-2.5">
            <TaskKey briefId={task.briefId}>{task.key}</TaskKey>
            <span className="line-clamp-2 min-w-0 text-base sm:truncate" title={task.title}>
              {task.title}
            </span>
            {task.pausedAt ? <PausedChip className="self-center" /> : null}
          </div>
          {/* The worker rides in the sub-line rather than in a column of its own:
              most tasks are unassigned, and a column that is empty on eight rows
              out of ten is a hole between the repo and the age. */}
          {note || task.assignedWorkerName ? (
            <p className={cn('mt-1 truncate text-xs', held ? 'text-warn' : 'text-fg-3')}>
              {note}
              {note && task.assignedWorkerName ? ' · ' : ''}
              {task.assignedWorkerName ?? ''}
            </p>
          ) : null}
          <span className="mt-1.5 flex items-center gap-2 sm:hidden">
            <RepoChip repo={task.repo} />
            <PrMark prUrl={task.prUrl} prDraft={task.prDraft} branch={task.branch} />
          </span>
        </div>
        {/* Three fixed columns, so repo, the branch/PR mark and age line up down
            the whole list instead of ragging with the title above them. The mark
            is not a link here: the row is already one, and a nested `<a>` would
            both be invalid HTML and eat the row's own click. */}
        <div className="flex shrink-0 items-center gap-3">
          <span className="hidden w-32 truncate sm:block">
            <RepoChip repo={task.repo} />
          </span>
          <span className="hidden w-28 truncate sm:block">
            <PrMark prUrl={task.prUrl} prDraft={task.prDraft} branch={task.branch} />
          </span>
          <span className="w-14 text-right whitespace-nowrap">
            {closed && task.finishedAt ? (
              <time className="text-fg-3 tnum text-xs">{clockTime(task.finishedAt)}</time>
            ) : (
              <Age
                minutes={minutesSince(
                  isGate ? (task.reviewEnteredAt ?? task.updatedAt) : task.updatedAt,
                )}
                hot={isGate}
              />
            )}
          </span>
        </div>
      </Link>
    </li>
  );
}

/**
 * How far back `Closed` reaches, in the header with the other two filters.
 *
 * It began in the `Closed` lane head, on the reasoning that a window means
 * nothing to the four lanes above it. True, and it still cost more than it
 * saved: three buttons sitting inside one lane read as part of that lane's
 * contents, they moved with it when a filter dropped the lanes to its left, and
 * on the board they had to be a segmented control rather than a menu because
 * the lane clips a popup. In the header it is the same shape as the state and
 * repo menus, and it does not move.
 *
 * Which is why the option text names the group rather than the window: "7 days"
 * beside "All" and "All repos" reads as a filter over the whole page, and this
 * one narrows exactly one group.
 */
const RANGE_OPTIONS = TASK_RANGES.map((entry) => ({
  value: entry.value,
  label: `Closed ${entry.label.toLowerCase()}`,
}));

export interface TaskBoardView {
  /** The clause under "Jobs" while the board is the active view. */
  sub: string;
  /** The state menu, repo menu and sort menu, for the header's own actions slot. */
  actions: ReactNode;
  /** The board or stacked list, plus the peek pane -- the page body. */
  body: ReactNode;
}

export function TaskBoardSection({ children }: { children: (view: TaskBoardView) => ReactNode }) {
  const search = useSearch({ strict: false }) as JobsSearch;
  const navigate = useNavigate();
  const tasks = useTasks();

  const repo = search.repo ?? ALL_REPOS;
  const status = search.status ?? DEFAULT_TASK_STATUS;
  const range = search.range ?? DEFAULT_TASK_RANGE;
  const selectedId = search.task;

  /** Out of the peek and back to the board, keeping the filters that found it. */
  const closePeek = () => {
    void navigate({
      to: '.',
      search: (previous: JobsSearch) => ({ ...previous, task: undefined }),
      replace: true,
    });
  };

  /**
   * A filter is not a place, so it replaces the history entry rather than
   * stacking one per click: Back should leave the Jobs page, not walk you out
   * through six filter states. The URL still carries the whole view, so it is
   * still linkable and survives a reload.
   */
  const setSearch = (patch: Partial<JobsSearch>) => {
    void navigate({
      to: '.',
      search: (previous: JobsSearch) => ({ ...previous, ...patch }),
      replace: true,
    });
  };

  const all = tasks.data ?? [];
  const repos = reposOf(all);
  const filtered = repo === ALL_REPOS ? all : all.filter((task) => task.repo === repo);
  const rawGroups = taskPageGroups(filtered, { range, status });
  const sorting = useSort(taskSortOptions(), (task) => task.id);
  const groups = sortedTaskGroups(rawGroups, sorting.state);
  const shown = groups.reduce((total, group) => total + group.tasks.length, 0);
  const open = filtered.filter(
    (task) => task.status !== 'done' && task.status !== 'cancelled',
  ).length;
  const statusLabel = TASK_STATUS_FILTERS.find((filter) => filter.value === status)?.label;

  // The groups are five panels and one list to the eye, so they are one list to
  // the keyboard. The keys stand down under the sheet, which is modal.
  const wide = useWideLayout();
  const boardLayout = useBoardLayout();
  const { rowProps } = useRowKeys({
    ids: groups.flatMap((group) => group.tasks.map((task) => task.id)),
    enabled: wide || !selectedId,
  });

  const sub =
    status === DEFAULT_TASK_STATUS
      ? `${open} open${repo === ALL_REPOS ? '' : ` in ${repo}`} · grouped by state`
      : `${shown} ${statusLabel?.toLowerCase()}${repo === ALL_REPOS ? '' : ` in ${repo}`}`;

  const actions = (
    <>
      <Menu
        label="Filter by state"
        value={status}
        onChange={(value) =>
          setSearch({
            status: isTaskStatusFilter(value) && value !== DEFAULT_TASK_STATUS ? value : undefined,
          })
        }
        align="end"
        options={TASK_STATUS_FILTERS.map((filter) => ({
          value: filter.value,
          label: filter.label,
          hint: filter.statuses
            ? String(
                filtered.filter((task) =>
                  (filter.statuses as readonly string[]).includes(task.status),
                ).length,
              )
            : String(filtered.length),
        }))}
      />
      <Menu
        label="Filter by repo"
        value={repo}
        onChange={(value) => setSearch({ repo: value === ALL_REPOS ? undefined : value })}
        align="end"
        options={[
          { value: ALL_REPOS, label: 'All repos', hint: String(all.length) },
          ...repos.map((name) => ({
            value: name,
            label: name,
            hint: String(all.filter((task) => task.repo === name).length),
          })),
        ]}
      />
      <Menu
        label="How far back Closed reaches"
        value={range}
        onChange={(value) =>
          setSearch({
            range: isTaskRange(value) && value !== DEFAULT_TASK_RANGE ? value : undefined,
          })
        }
        align="end"
        options={RANGE_OPTIONS}
      />
      <SortMenu options={sorting.options} state={sorting.state} onChange={sorting.choose} />
    </>
  );

  const body = (
    <SplitPane
      list={
        tasks.isPending && !all.length ? (
          <Section>
            <SectionHead title="Waiting on a human" />
            <Surface>
              <RowSkeleton rows={4} />
            </Surface>
          </Section>
        ) : boardLayout ? (
          /**
           * The lanes, in the height the viewport has left.
           *
           * `100svh` minus the header (4rem) and the page's own top and
           * bottom padding (1.5 and 2.5rem), which is the whole of what is
           * above and below this element. The floor is there for a short
           * window: below it the board would be four cards tall and the
           * page may as well scroll.
           */
          <div className="flex h-[calc(100svh-8rem)] min-h-[28rem] flex-col">
            <StepBoard groups={groups} selectedId={selectedId} rowProps={rowProps} />
          </div>
        ) : (
          groups.map((group) => (
            <Section key={group.key}>
              <SectionHead
                title={group.label}
                meta={group.tasks.length ? String(group.tasks.length) : undefined}
                hue={group.key === 'gate' && group.tasks.length ? 'var(--dy-gate)' : undefined}
              />
              <Surface>
                {group.tasks.length ? (
                  <ul className="divide-line divide-y">
                    {group.tasks.map((task) => (
                      <TaskRow
                        key={task.id}
                        task={task}
                        selected={selectedId === task.id}
                        rowProps={rowProps(task.id)}
                      />
                    ))}
                  </ul>
                ) : (
                  <p className="text-fg-3 px-gutter py-6 text-center text-sm">{group.empty}</p>
                )}
              </Surface>
            </Section>
          ))
        )
      }
      peekEmpty={!selectedId}
      onClose={closePeek}
      overlay
      peek={selectedId ? <TaskDetail taskId={selectedId} /> : null}
    />
  );

  return children({ sub, actions, body });
}
